class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.predictiveSwiper = null;
    this.noResultSwiper = null;

    this.intilizeSwiper = this.intilizeSwiper.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);

    this.setupEventListeners();
  }

  connectedCallback() {
    requestAnimationFrame(() => this.intilizeSwiper());
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // ✅ Close on outside click (.template-search included)
    document.addEventListener('click', this.handleOutsideClick);

    // ✅ Prevent inside clicks from closing
    this.addEventListener('click', e => e.stopPropagation());

    window.addEventListener('resize', () => this.intilizeSwiper());

    // Re-init after section reload in theme editor
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', () => {
        requestAnimationFrame(() => this.intilizeSwiper());
      });
    }
  }

  handleOutsideClick(event) {
    if (!this.isOpen) return;
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const el = this.querySelector('[data-predictive-search-search-for-text]');
    if (!el || !previousTerm) return;

    const matches = el.innerText.match(new RegExp(previousTerm, 'g'));
    if (matches && matches.length > 1) return;

    el.innerText = el.innerText.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selected = this.querySelector('[aria-selected="true"]');

    const items = Array.from(
      this.querySelectorAll('li, button.predictive-search__item')
    ).filter(el => el.offsetParent !== null);

    if (moveUp && !selected) return;

    let index = 0;
    let selectedIndex = items.indexOf(selected);

    if (!moveUp && selected) {
      index = selectedIndex === items.length - 1 ? 0 : selectedIndex + 1;
    } else if (moveUp) {
      index = selectedIndex === 0 ? items.length - 1 : selectedIndex - 1;
    }

    if (index === selectedIndex) return;

    selected?.setAttribute('aria-selected', false);
    items[index].setAttribute('aria-selected', true);
    this.input.setAttribute('aria-activedescendant', items[index].id);
  }

  selectOption() {
    const el = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    el?.click();
  }

  getSearchResults(searchTerm) {
    const key = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[key]) {
      this.renderSearchResults(this.cachedResults[key]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&resources[options][fields]=title,product_type,tag&section_id=predictive-search`,
      { signal: this.abortController.signal }
    )
      .then(res => {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(text => {
        const html = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        this.allPredictiveSearchInstances.forEach(instance => {
          instance.cachedResults[key] = html;
        });

        this.renderSearchResults(html);
      })
      .catch(err => {
        if (err?.code === 20) return;
        this.close();
      });
  }

  renderSearchResults(markup) {
    if (this.predictiveSwiper) {
      this.predictiveSwiper.destroy(true, true);
      this.predictiveSwiper = null;
    }

    if (this.noResultSwiper) {
      this.noResultSwiper.destroy(true, true);
      this.noResultSwiper = null;
    }

    this.predictiveSearchResults.innerHTML = markup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();

    requestAnimationFrame(() => this.intilizeSwiper());
  }

  intilizeSwiper() {
    const isMobile = window.innerWidth <= 1023;
    const swiperConfig = {
      slidesPerView: 'auto',
      spaceBetween: 8,
      freeMode: true,
      slidesOffsetBefore: 16,
      slidesOffsetAfter: 16,
      watchOverflow: true,
      freeModeMomentumBounce: false,
    };

    const container = this.querySelector('.predictive-search__products-list');
    if (container) {
      if (isMobile && !this.predictiveSwiper) {
        this.predictiveSwiper = new Swiper(container, swiperConfig);
      } else if (!isMobile && this.predictiveSwiper) {
        this.predictiveSwiper.destroy(true, true);
        this.predictiveSwiper = null;
      }
    }

    const noResultContainer = this.querySelector('.no-result__suggestions-swiper');
    if (noResultContainer) {
      if (isMobile && !this.noResultSwiper) {
        this.noResultSwiper = new Swiper(noResultContainer, { ...swiperConfig, centerInsufficientSlides: true });
      } else if (!isMobile && this.noResultSwiper) {
        this.noResultSwiper.destroy(true, true);
        this.noResultSwiper = null;
      }
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(text) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = text;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(
      this.querySelector('[data-predictive-search-live-region-count-value]').textContent
    );
  }

  getResultsMaxHeight() {
    return (
      window.innerHeight -
      document.querySelector('.search-modal__form').getBoundingClientRect().bottom
    );
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clear = false) {
    if (clear) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    this.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.predictiveSearchResults.removeAttribute('style');
    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
